import { Component } from '@theme/component';
import { onAnimationEnd } from '@theme/utilities';
import { ThemeEvents } from '@theme/events';
import { StandardEvents, CartLinesUpdateEvent } from '@shopify/events';

const GFE_GIFT_WRAP_PROPERTY = '_gfe_gift_wrap';
const GFE_GIFT_WRAP_SOURCE = 'gfe-gift-wrap';

function getCartRoot() {
  return window.Shopify?.routes?.root || '/';
}

function getVisibleItemCount(cart) {
  return (cart?.items || []).reduce((count, item) => {
    const isGiftWrap =
      item?.properties?.[GFE_GIFT_WRAP_PROPERTY] === 'true' ||
      item?.properties?.[GFE_GIFT_WRAP_PROPERTY] === true;

    return isGiftWrap ? count : count + Number(item?.quantity || 0);
  }, 0);
}

async function fetchVisibleItemCount() {
  const response = await fetch(`${getCartRoot()}cart.js`, {
    headers: { Accept: 'application/json' },
    cache: 'no-store',
  });

  if (!response.ok) throw new Error('Kurven kunne ikke genindlæses.');

  return getVisibleItemCount(await response.json());
}

/**
 * A custom element that displays a cart icon.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} cartBubble - The cart bubble element.
 * @property {HTMLElement} cartBubbleText - The cart bubble text element.
 * @property {HTMLElement} cartBubbleCount - The cart bubble count element.
 *
 * @extends {Component<Refs>}
 */
class CartIcon extends Component {
  requiredRefs = ['cartBubble', 'cartBubbleText', 'cartBubbleCount'];

  /** @type {number} */
  get currentCartCount() {
    return parseInt(this.refs.cartBubbleCount.textContent ?? '0', 10);
  }

  set currentCartCount(value) {
    this.refs.cartBubbleCount.textContent = value < 100 ? String(value) : '';
  }

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(StandardEvents.cartLinesUpdate, this.onCartUpdate);
    window.addEventListener('pageshow', this.onPageShow);

    // GFE: synkronisér altid badge med den faktiske kurv ved indlæsning.
    // Det forhindrer et midlertidigt antal på fx. "1" efter en tilføjelse.
    this.syncVisibleCartCount(false);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(StandardEvents.cartLinesUpdate, this.onCartUpdate);
    window.removeEventListener('pageshow', this.onPageShow);
  }

  /**
   * Handles the page show event when the page is restored from cache.
   * @param {PageTransitionEvent} event - The page show event.
   */
  onPageShow = () => {
    this.syncVisibleCartCount(false);
  };

  /**
   * Handles the cart update event.
   * @param {CartLinesUpdateEvent} event - The cart update event.
   */
  onCartUpdate = (event) => {
    event.promise
      ?.then(async ({ cart, detail }) => {
        // GFE's egen gaveindpakningsopdatering leverer allerede det korrekte
        // synlige antal. Ved almindelig Add to cart henter vi hele kurven,
        // fordi standard-eventet ellers kan indeholde kun den netop tilføjede
        // mængde (fx. 1) i stedet for kurvens samlede antal.
        if (detail?.source === GFE_GIFT_WRAP_SOURCE && Number.isFinite(Number(detail?.itemCount))) {
          await this.renderCartBubble(Number(detail.itemCount));
          return;
        }

        try {
          const itemCount = await fetchVisibleItemCount();
          await this.renderCartBubble(itemCount);
        } catch (error) {
          const fallbackCount = Number(cart?.totalQuantity ?? detail?.itemCount ?? 0);
          await this.renderCartBubble(Number.isFinite(fallbackCount) ? fallbackCount : 0);
          console.warn('[cart-icon] Could not refresh visible cart count:', error);
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-icon] Event promise rejected:', error);
      });
  };

  /**
   * Reads the complete cart and updates the badge with visible items only.
   * @param {boolean} [animate=false]
   */
  syncVisibleCartCount = async (animate = false) => {
    try {
      const itemCount = await fetchVisibleItemCount();
      await this.renderCartBubble(itemCount, animate);
    } catch (error) {
      console.warn('[cart-icon] Could not synchronize visible cart count:', error);
    }
  };

  /**
   * Renders the cart bubble.
   * @param {number} itemCount - The absolute number of items in the cart.
   * @param {boolean} [animate=true] - Whether to animate the bubble.
   */
  renderCartBubble = async (itemCount, animate = true) => {
    this.refs.cartBubbleCount.classList.toggle('hidden', itemCount === 0);
    this.refs.cartBubble.classList.toggle('visually-hidden', itemCount === 0);

    this.currentCartCount = itemCount;

    this.classList.toggle('header-actions__cart-icon--has-cart', itemCount > 0);

    sessionStorage.setItem(
      'cart-count',
      JSON.stringify({
        value: String(this.currentCartCount),
        timestamp: Date.now(),
      })
    );

    if (!animate || itemCount === 0) return;

    // Ensure element is visible before starting animation
    // Use requestAnimationFrame to ensure the browser sees the state change
    await new Promise((resolve) => requestAnimationFrame(resolve));

    this.refs.cartBubble.classList.add('cart-bubble--animating');
    await onAnimationEnd(this.refs.cartBubbleText);

    this.refs.cartBubble.classList.remove('cart-bubble--animating');
  };

  /**
   * Checks if the cart count is correct.
   */
  ensureCartBubbleIsCorrect = () => {
    // Ensure refs are available
    if (!this.refs.cartBubbleCount) return;

    const sessionStorageCount = sessionStorage.getItem('cart-count');

    // If no session storage data, nothing to check
    if (sessionStorageCount === null) return;

    const visibleCount = this.refs.cartBubbleCount.textContent;

    try {
      const { value, timestamp } = JSON.parse(sessionStorageCount);

      // Check if the stored count matches what's visible
      if (value === visibleCount) return;

      // Only update if timestamp is recent (within 10 seconds)
      if (Date.now() - timestamp < 10000) {
        const count = parseInt(value, 10);

        if (count >= 0) {
          this.renderCartBubble(count, false);
        }
      }
    } catch (_) {
      // no-op
    }
  };
}

if (!customElements.get('cart-icon')) {
  customElements.define('cart-icon', CartIcon);
}
